import { useApolloClient } from "@apollo/client";
import { MenuItem, Text, Tooltip } from "@chakra-ui/react";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import { maybePluralize } from "src/services/format";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import { FormSchoolStatus } from "../types";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  onRefetch?: () => void;
  isBulkAction?: boolean;
}

const CONFIRMATION_BODY_TEXT =
  "Clearing the sub-status will delete any offer or waitlist information, including acceptances, programs or waitlists positions. This can’t be undone nor recovered.";

const DISABLED_TOOLTIP_TEXT =
  "This form is associated with another, revoke the offer or remove from waitlist instead";

export const ClearSubstatus = ({
  selectedFormSchoolStatuses,
  onRefetch,
  isBulkAction = false,
}: Props) => {
  const apolloClient = useApolloClient();
  const { mutateAsync: clearSubstatus } = FSQ.useClearSubStatus();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const toast = useAvelaToast();

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "Cancel",
      },
      confirmButton: {
        label: "Yes, clear",
        colorScheme: "blue",
      },
      translate: true,
    });

  const handleOnClick = React.useCallback(
    async (evt: React.MouseEvent) => {
      const schoolForms = selectedFormSchoolStatuses;
      const count = schoolForms.length;
      const trackArgs = {
        actionType: "CLEAR_SUBSTATUS" as const,
        bulk: isBulkAction,
        count,
        async: false,
      };

      const itemNoun = maybePluralize(count, "item");
      setHeader(<Text>{`Clear sub-status from ${count} ${itemNoun}?`}</Text>);
      setBody(<Text>{CONFIRMATION_BODY_TEXT}</Text>);

      trackFormAction({ verb: "Clicked", ...trackArgs });

      if (await confirm()) {
        trackFormAction({ verb: "Confirmed", ...trackArgs });

        const loadingToastId = toast({
          title: `Working hard`,
          description: `Clearing sub-status from ${count} ${itemNoun}...`,
          status: "loading",
          duration: undefined,
        });

        try {
          await clearSubstatus({
            body: {
              data: {
                form_schools: schoolForms.map((schoolForm) => ({
                  form_school_rank_id: schoolForm.id,
                  form_id: schoolForm.form_id,
                  school_id: schoolForm.school_id,
                })),
              },
            },
          });
          await apolloClient.refetchQueries({
            include: [
              FETCH_OFFER_WAITLIST_STATUS,
              GET_ASSIGNED_FORMS_BY_FORM_ID,
            ],
          });

          if (onRefetch) onRefetch();

          toast.close(loadingToastId);
          toast({
            title: "Hooray",
            description: `${count} ${itemNoun} cleared`,
            status: "success",
          });
          trackFormAction({ verb: "Completed", ...trackArgs });
        } catch (err: any) {
          trackFormActionError(err, trackArgs);
          toast.close(loadingToastId);
          toast.error({
            title: "Oops! Something went wrong!",
            description: "Check your network and try again.",
          });
          console.error(err);
        }
      } else {
        trackFormAction({ verb: "Canceled", ...trackArgs });
      }
    },
    [
      selectedFormSchoolStatuses,
      setHeader,
      setBody,
      confirm,
      toast,
      clearSubstatus,
      apolloClient,
      isBulkAction,
      onRefetch,
    ]
  );

  const hasAssignedForms = selectedFormSchoolStatuses.some(
    (result) =>
      (result.offers?.[0] && result.offers?.[0].assigned_forms.length > 0) ||
      (result.waitlists?.[0] && result.waitlists?.[0].assigned_forms.length > 0)
  );
  const canClear =
    !hasAssignedForms &&
    selectedFormSchoolStatuses.every((result) =>
      isSubStatusTransitionAllowed(result.sub_status, null)
    );

  return (
    <>
      <Tooltip
        label={<Text align="center">{DISABLED_TOOLTIP_TEXT}</Text>}
        placement="left"
        hasArrow
        arrowSize={15}
        gutter={20}
        minW="22rem"
        isDisabled={!hasAssignedForms}
      >
        <MenuItem
          textColor="gray.700"
          fontWeight="400"
          fontSize="sm"
          onClick={handleOnClick}
          isDisabled={!canClear}
        >
          Clear sub-status
        </MenuItem>
      </Tooltip>
      {confirmationDialog}
    </>
  );
};
